<template>
  <v-app>
    <v-app-bar app elevation="1" color="#00b687">
      <v-toolbar-title class="text-h5 font-weight-bold white--text">SpeakUp Infosec</v-toolbar-title>
      <v-spacer></v-spacer>
      <version-display class="version-top-right white--text" />
    </v-app-bar>

    <v-main class="grey lighten-4">
      <v-container fluid class="pa-6">
        <v-row v-if="!isLoggedIn">
          <v-col cols="12" md="6" offset-md="3">
            <v-card class="elevation-2 rounded-lg">
              <v-card-title class="text-h5 font-weight-bold pb-2">Login</v-card-title>
              <v-card-text>
                <LoginForm @login-success="handleLoginSuccess" @login-error="showError" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <template v-else>
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="elevation-2 rounded-lg">
                <v-card-title class="text-h5 font-weight-bold pb-2">Ask a Question</v-card-title>
                <v-card-text>
                  <QuestionForm 
                    :access-token="accessToken" 
                    @response-received="handleResponse"
                    @authentication-error="handleApiError({ response: { status: 401 } })"
                  />
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card class="elevation-2 rounded-lg mb-6">
                <v-card-title class="text-h5 font-weight-bold pb-2">
                  Summary
                </v-card-title>
                <v-card-text>
                  <div v-if="coverage !== null" class="mb-4 d-flex align-center">
                    <strong class="mr-2">Coverage: </strong>
                    <v-rating
                      :model-value="coverageRating"
                      :color="ratingColor"
                      background-color="grey lighten-3"
                      empty-icon="mdi-star-outline"
                      full-icon="mdi-star"
                      half-icon="mdi-star-half-full"
                      readonly
                      length="5"
                      dense
                    ></v-rating>
                    <span :class="['ml-2', coverageColor]">
                      {{ coverageText }} ({{ coverage }}%)
                    </span>
                  </div>
                  <p class="text-body-1">{{ summary || 'No summary available' }}</p>
                </v-card-text>
                <v-card-actions v-if="summary">
                  <v-btn
                    text
                    @click="copySummary"
                    :disabled="copying"
                    color="#00b687"
                    class="text-none"
                  >
                    <v-icon left>{{ copying ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                    {{ copying ? 'Copied!' : 'Copy Summary' }}
                  </v-btn>
                </v-card-actions>
                <v-card-text>
                  <div class="chip-container">
                    <v-chip
                      v-for="(ref, index) in references"
                      :key="index"
                      @click="handleChipClick(ref)"
                      class="ma-1"
                      color="#00b687"
                      text-color="white"
                      variant="elevated"
                      size="small"
                    >
                      {{ getChipLabel(ref) }}
                      <v-tooltip activator="parent" location="bottom">
                        <span v-html="escapeHtml(ref.quote)"></span>
                      </v-tooltip>
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
              
              <v-card v-if="showMarkdownContent" class="elevation-2 rounded-lg">
                <v-card-title class="text-h5 font-weight-bold pb-2">Procedure</v-card-title>
                <v-card-text>
                  <v-tabs
                    v-model="activeTab"
                    color="#00b687"
                    align-tabs="center"
                  >
                    <v-tab v-for="file in displayedFiles" :key="file">
                      {{ getFileAbbreviation(file) }}
                    </v-tab>
                  </v-tabs>
                  <v-window v-model="activeTab">
                    <v-window-item v-for="file in displayedFiles" :key="file">
                      <div class="markdown-container pa-4" style="height: 400px; overflow-y: auto;" ref="markdownContainer">
                        <MarkdownViewer 
                          v-if="markdownContent[file]"
                          :content="markdownContent[file]"
                          :highlighted-sentences="highlightedSentences"
                          :reference-number="currentReferenceNumber"
                          @highlight-ready="scrollToFirstHighlight"
                        />
                        <v-progress-circular v-else indeterminate color="primary"></v-progress-circular>
                      </div>
                    </v-window-item>
                  </v-window>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-container>
    </v-main>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="5000"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          color="white"
          text
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { onBeforeUnmount } from 'vue';
import QuestionForm from './components/QuestionForm.vue'
import MarkdownViewer from './components/MarkdownViewer.vue'
import LoginForm from './components/LoginForm.vue'
import VersionDisplay from '@/components/VersionDisplay.vue';

export default {
  name: 'App',
  components: {
    QuestionForm,
    MarkdownViewer,
    LoginForm,
    VersionDisplay
  },
  data: () => ({
    isLoggedIn: false,
    accessToken: null,
    response: null,
    summary: null,
    references: [],
    activeTab: null,
    markdownFiles: [],
    markdownContent: {},
    highlightedSentences: [],
    currentReferenceNumber: null,
    snackbar: {
      show: false,
      message: '',
      color: 'error',
    },
    copying: false,
    coverage: null,
    referencedFiles: [],
    showMarkdownContent: false,
  }),
  computed: {
    coverageColor() {
      if (this.coverage === null) return '';
      if (this.coverage < 10) return 'red--text';
      if (this.coverage > 80) return 'green--text';
      return 'orange--text';
    },
    coverageRating() {
      if (this.coverage === null) return 0;
      return Math.round(this.coverage / 20);
    },
    coverageText() {
      if (this.coverage === null) return '';
      if (this.coverage < 10) return 'not covered';
      if (this.coverage > 80) return 'fully covered';
      return 'partially covered';
    },
    ratingColor() {
      if (this.coverage < 10) return 'red';
      if (this.coverage > 80) return 'green';
      return 'amber';
    },
    displayedFiles() {
      return this.referencedFiles;
    }
  },
  methods: {
    async handleLoginSuccess(token) {
      this.accessToken = token;
      this.isLoggedIn = true;
      localStorage.setItem('jwt_token', token); // Store token in localStorage
      this.showSuccess('Login successful');
      await this.loadMarkdownFiles();
    },
    async checkStoredToken() {
      const storedToken = localStorage.getItem('jwt_token');
      if (storedToken) {
        this.accessToken = storedToken;
        this.isLoggedIn = true;
        await this.loadMarkdownFiles();
      }
    },
    logout() {
      this.accessToken = null;
      this.isLoggedIn = false;
      localStorage.removeItem('jwt_token');
      // Reset other necessary state variables
      this.response = null;
      this.summary = null;
      this.references = [];
      // ... reset other relevant data
    },
    async handleApiError(error) {
      if (error.response && error.response.status === 401) {
        this.showError('Authentication failed. Please log in again.');
        this.logout();
      } else {
        this.showError('An error occurred. Please try again.');
      }
    },
    async handleResponse(data, rawResponse, status) {
      if (status === 200) {
        this.response = rawResponse;
        this.summary = data.summary || 'No summary available';
        this.references = data.references || [];
        this.coverage = data.coverage !== undefined ? data.coverage : null;
        
        // Update referencedFiles based on the response, using full filenames
        this.referencedFiles = [...new Set(this.references.map(ref => {
          // Find the full filename that matches the document reference
          return this.markdownFiles.find(file => file.includes(ref.document)) || ref.document;
        }))];
        
        // Show markdown content if there are references
        this.showMarkdownContent = this.referencedFiles.length > 0;
        
        if (this.showMarkdownContent) {
          // Set activeTab to the first referenced file
          this.activeTab = 0;
          
          console.log('Referenced files:', this.referencedFiles);
          console.log('Markdown content:', this.markdownContent);
        }
        
        this.currentReferenceNumber = null; // Reset the current reference number
        this.highlightedSentences = []; // Reset highlighted sentences
        
        // Force a re-render of the component
        this.$forceUpdate();
      } else {
        // Show error in snackbar
        this.showError(`Error ${status}: ${data.error || 'Unknown error occurred'}`);
      }
    },
    showError(message) {
      this.snackbar.message = message;
      this.snackbar.show = true;
    },
    handleChipClick(ref) {
      console.log('Clicked reference:', ref);
      const fullFilename = this.markdownFiles.find(file => file.includes(ref.document)) || ref.document;
      const fileIndex = this.displayedFiles.indexOf(fullFilename);
      if (fileIndex !== -1) {
        this.activeTab = fileIndex;
        this.highlightedSentences = this.splitIntoSentences(ref.quote);
        this.currentReferenceNumber = ref.paragraph;
        // We'll scroll to the highlight after the component updates
        this.$nextTick(() => {
          this.scrollToFirstHighlight();
        });
      } else {
        console.error(`No matching tab found for document: ${ref.document}`);
      }
    },
    scrollToFirstHighlight() {
      if (!this.$refs.markdownContainer || !this.$refs.markdownContainer[this.activeTab]) {
        console.error('Markdown container not found');
        return;
      }
      const container = this.$refs.markdownContainer[this.activeTab];
      const highlightedElement = container.querySelector('.highlighted-quote');
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        console.warn('No highlighted element found');
      }
    },
    async copySummary() {
      if (!this.summary) return;
      
      this.copying = true;
      try {
        await navigator.clipboard.writeText(this.summary);
        this.showSuccess('Summary copied to clipboard');
      } catch (err) {
        this.showError('Failed to copy summary');
      } finally {
        setTimeout(() => {
          this.copying = false;
        }, 2000);
      }
    },
    showSuccess(message) {
      this.snackbar.color = 'success';
      this.snackbar.message = message;
      this.snackbar.show = true;
    },
    async loadMarkdownFiles() {
      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/files`, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        });
        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication failed');
          }
          throw new Error('Failed to fetch file list');
        }
        this.markdownFiles = await response.json();
        
        // Fetch content for each file
        for (const file of this.markdownFiles) {
          await this.fetchFileContent(file);
        }
      } catch (error) {
        console.error('Error loading markdown files:', error);
        await this.handleApiError(error);
      }
    },
    async fetchFileContent(filename) {
      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/files/${filename}`, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        });
        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Authentication failed');
          }
          throw new Error(`Failed to fetch content for ${filename}`);
        }
        const content = await response.text();
        // Update markdownContent reactively
        this.markdownContent = { ...this.markdownContent, [filename]: content };
        console.log(`Content loaded for ${filename}`); // Add this line for debugging
      } catch (error) {
        console.error(`Error fetching content for ${filename}:`, error);
        await this.handleApiError(error);
      }
    },
    getFileAbbreviation(filename) {
      const parts = filename.split('%20');
      if (parts.length > 1 && parts[1].length >= 3) {
        return parts[1].substring(0, 3).toUpperCase();
      }
      return filename.substring(0, 3).toUpperCase(); // Fallback if the filename doesn't match expected format
    },
    splitIntoSentences(text) {
      return text.match(/[^.!?]+[.!?]+/g) || [text];
    },
    getChipLabel(ref) {
      const fullFilename = this.markdownFiles.find(file => file.includes(ref.document)) || ref.document;
      const abbr = this.getFileAbbreviation(fullFilename);
      const paragraph = ref.paragraph;
      return `${abbr} - ${paragraph}`;
    },
    escapeHtml(unsafe) {
      return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
  },
  mounted() {
    this.checkStoredToken(); // Check for stored token on mount

    // Create a custom ResizeObserver
    const originalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class ResizeObserver extends originalResizeObserver {
      constructor(callback) {
        super((entries, observer) => {
          // Wrap the callback in a try-catch to prevent the error from being thrown
          try {
            callback(entries, observer);
          } catch (e) {
            if (e.message.includes('ResizeObserver loop')) {
              console.warn('Ignored ResizeObserver error');
            } else {
              throw e;
            }
          }
        });
      }
    };

    // Use onBeforeUnmount hook to restore the original ResizeObserver
    onBeforeUnmount(() => {
      window.ResizeObserver = originalResizeObserver;
    });

    console.log('Initial markdownContent:', this.markdownContent);
  },
}
</script>

<style>
.highlighted-quote {
  background-color: yellow;
  font-weight: bold;
}

.v-tooltip__content {
  max-width: 300px;
  white-space: normal;
  word-break: break-word;
}

.chip-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.response-container {
  width: 100%;
  max-height: 400px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 16px;
}

.response-container pre {
  margin: 0;
  font-family: 'Roboto Mono', monospace;
}

.v-tooltip .v-overlay__content {
  background-color: rgba(97, 97, 97, 0.9);
  color: white;
  border-radius: 4px;
  font-size: 12px;
  padding: 5px 8px;
  max-width: 300px;
  white-space: normal;
  word-break: break-word;
}

.version-top-right {
  margin-right: 16px;
}

.markdown-container {
  background-color: #ffffff;
  border-radius: 4px;
}

.v-card {
  transition: box-shadow 0.3s ease-in-out;
}

.v-card:hover {
  box-shadow: 0 6px 12px rgba(0,0,0,0.1) !important;
}

.v-btn {
  text-transform: none !important;
}
</style>
